/*
 * Copyright 2026 The Kubeflow Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useCallback, useState } from 'react';

type KeyedState<T> = {
  key: string;
  value: T;
};

export function useKeyedState<T>(key: string, initialValue: T) {
  const [state, setState] = useState<KeyedState<T>>({ key: '', value: initialValue });
  const value = state.key === key ? state.value : initialValue;
  const setValue = useCallback((nextValue: T) => setState({ key, value: nextValue }), [key]);

  return [value, setValue] as const;
}
